import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { Suspense } from "react";
import { z } from "zod";
import { serversQueryOptions } from "@/app/servers/api/get-servers";
import { ServersListSkeleton } from "@/app/servers/components/servers-list-skeleton";
import { ServersList } from "@/app/servers/list/list";
import { nameFilterSchema } from "@/components/custom/filters/name-filter";
import {
  scopeFilterSearchDefaults,
  scopeFilterSearchSchema,
} from "@/components/custom/resources/scope-filter";

const serverTypeSchema = z.enum(["list", "new"]);
const serversViewSchema = z.enum(["servers", "browse"]);

const serversParamsSchema = z.object({
  type: serverTypeSchema.catch("list").default("list"),
  view: serversViewSchema.catch("servers").default("servers"),
  // The Browse tab's search. Held in the URL like the tab itself, so a
  // filtered catalog survives a reload and can be linked to.
  q: z.string().catch("").default(""),
  defaultValues: z
    .object({
      id: z.string().optional(),
      uri: z.string().optional(),
      secret: z.string().optional(),
      timeout: z.number().optional(),
      retry: z.number().optional(),
    })
    .optional()
    .catch({
      id: "",
      uri: "",
      secret: "",
      timeout: 30,
      retry: 3,
    })
    .default({
      id: "",
      uri: "",
      secret: "",
      timeout: 30,
      retry: 3,
    }),
  serverTypes: z.array(z.string()).catch([]).default([]),
  statuses: z.array(z.string()).catch([]).default([]),
  ...nameFilterSchema.shape,
  ...scopeFilterSearchSchema.shape,
});

const defaultValues = {
  type: "list" as const,
  view: "servers" as const,
  q: "",
  name: "",
  serverTypes: [],
  statuses: [],
  defaultValues: {
    id: "",
    uri: "",
    secret: "",
    timeout: 30,
    retry: 3,
  },
  ...scopeFilterSearchDefaults,
};

export const Route = createFileRoute(
  "/_authenticated/orgs/$organizationId/projects/$projectId/servers/"
)({
  validateSearch: serversParamsSchema,
  search: {
    middlewares: [stripSearchParams(defaultValues)],
  },
  component: (): React.JSX.Element => (
    <Suspense fallback={<ServersListSkeleton />}>
      <ServersList />
    </Suspense>
  ),
  beforeLoad: ({ context: { queryClient }, params }) => {
    queryClient.prefetchQuery(
      serversQueryOptions({ binding: "project", scope: params })
    );
  },
  loader: () => ({
    crumb: "Servers",
  }),
});
